import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";

export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function readCompanyLogo(file) {
  return new Promise((resolve, reject) => {
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > MAX_LOGO_BYTES) {
      reject(new Error("Choose a PNG or JPEG image up to 2 MB."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The image could not be read."));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error("Choose a valid PNG or JPEG image."));
      image.onload = () => {
        if (Math.max(image.width, image.height) > 4096 || image.width * image.height > 16000000) {
          reject(new Error("Choose an image no larger than 4096 pixels per side."));
          return;
        }
        resolve({ logoDataUrl: reader.result, logoFileName: file.name });
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ProjectReportLogo({ value, onChange }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const id = ++request.current;
    setError("");
    setLoading(true);
    try {
      const logo = await readCompanyLogo(file);
      if (id === request.current) onChange(logo);
    } catch (err) {
      if (id === request.current) setError(err.message);
    } finally {
      if (id === request.current) setLoading(false);
    }
  };
  return <Stack spacing={1}>
    <Typography variant="subtitle2">Company logo for reports</Typography>
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box component="img" src={value?.logoDataUrl || "/logo.png"} alt={value?.logoDataUrl ? "Company report logo" : "Default MetEn logo"}
        sx={{ width: 140, height: 72, objectFit: "contain", bgcolor: "white", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }} />
      <Button component="label" variant="outlined" disabled={loading}>
        {loading ? "Reading image…" : value?.logoDataUrl ? "Replace logo" : "Upload logo"}
        <input aria-label="Upload company logo" type="file" accept="image/png,image/jpeg" hidden onChange={upload} disabled={loading} />
      </Button>
      {value?.logoDataUrl && <Button disabled={loading} onClick={() => { setError(""); onChange({}); }}>Use MetEn default</Button>}
    </Stack>
    {value?.logoFileName && <Typography variant="caption">{value.logoFileName}</Typography>}
    <Typography variant="caption" color="text.secondary">PNG or JPEG, up to 2 MB. Save the project to keep this logo for all its assets. Used in newly generated PDF reports; existing reports keep their original logo.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
  </Stack>;
}
